import { Select, Spin } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { defaultFilterOption } from "shared/lib/selectFilter";
import { getCookie } from "typescript-cookie";
import { EntTerritorialsUnits } from "features/territorialUnit/model/territorialUnits";
import { EntTerritorialUnitsFilter } from "features/territorialUnit/model/territorialUnitsFilter";
import { useLazyGetTerritorialUnitsListQuery } from "features/territorialUnit/api/territorialUnitsApi";
import { useGetDataOnScroll } from "features/territorialUnit/hooks/useGetDataOnScroll";

interface IProps {
  value?: EntTerritorialsUnits | null;
  type?: (
    | "AREA"
    | "REGION"
    | "CITY_OF_REPUBLICAN_SIGNIFICANCE"
    | "CITY_OF_REGIONAL_SIGNIFICANCE"
  )[];
  onChange?: (value: EntTerritorialsUnits | null) => void;
  status?: any;
  parentId?: number | null;
  areaCode?: string | null;
  className?: string;
  placeholder?: string;
  size?: "small" | "large";
  style?: React.CSSProperties;
}

const SelectTerritorialUnitsCode = (props: IProps) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<
    { label: string; value: number; data: EntTerritorialsUnits }[]
  >([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [getData] = useLazyGetTerritorialUnitsListQuery();
  const {
    fetching,
    data,
    setData,
    setFetching,
    setFilter,
    scrollHandler,
    setCurrentPage,
  } = useGetDataOnScroll<EntTerritorialsUnits, EntTerritorialUnitsFilter>({
    initialFilter: new EntTerritorialUnitsFilter({
      type: props.type,
      parentId: props.parentId,
      areaCode: props.areaCode,
      id: props.value?.id,
    }),
    query: getData,
  });

  useEffect(() => {
    const opts = data.map((a) => ({
      label: getCookie("i18next") === "ky" ? a.nameKy || "" : a.nameRu || "",
      value: a.id ?? 0,
      data: a,
    }));
    setOptions(opts);
  }, [data]);

  useEffect(() => {
    if (props.type) {
      setFetching(true);
    }
  }, [props.type]);

  useEffect(() => {
    if (!isDropdownOpen) return;

    const filter = new EntTerritorialUnitsFilter();
    filter.type = props.type ?? null;
    filter.parentId = props.parentId ?? null;
    filter.areaCode = props.areaCode ?? null;

    setFilter(filter);
    setData([]);
    setCurrentPage(0);
    setFetching(true);
  }, [isDropdownOpen, props.type, props.parentId, props.areaCode]);

  const search = (value?: string) => {
    const filter = new EntTerritorialUnitsFilter();
    filter.type = props.type ?? null;
    filter.parentId = props.parentId ?? null;
    filter.areaCode = props.areaCode ?? null;

    if (!value) {
      filter.name = null;
      setFilter(filter);
      setData([]);
      setCurrentPage(0);
      setFetching(true);
      return;
    }

    filter.name = value;
    setFilter(filter);
    setData([]);
    setCurrentPage(0);
    setFetching(true);
  };

  const clear = () => {
    const filter = new EntTerritorialUnitsFilter();
    filter.name = null;
    filter.type = props.type ?? null;
    filter.parentId = props.parentId ?? null;
    filter.areaCode = props.areaCode ?? null;
    setFilter(filter);
    setData([]);
    setCurrentPage(0);
    setFetching(true);
    props.onChange?.(null);
  };

  return (
    <Select
      placeholder={props.placeholder ?? t("msg.selectPlaceholder")}
      onClear={clear}
      size={props.size}
      onSearch={search}
      className={props.className}
      onPopupScroll={scrollHandler}
      loading={fetching}
      value={props.value?.id || undefined}
      onChange={(val, option) => {
        if (Array.isArray(option)) return;
        const selectedOption = option as
          | { value: number; data: EntTerritorialsUnits }
          | undefined;
        const data = selectedOption?.data;
        if (data && data.id !== null) {
          props.onChange?.(data);
        } else {
          props.onChange?.(null);
        }
      }}
      options={options}
      status={props.status}
      filterOption={defaultFilterOption}
      showSearch
      allowClear
      style={{
        width: "100%",
        backgroundColor: "transparent",
        border: "none",
        boxShadow: "none",
        ...props.style,
      }}
      popupRender={(menu) => <Spin spinning={fetching}>{menu}</Spin>}
      onOpenChange={setIsDropdownOpen}
    />
  );
};

export default SelectTerritorialUnitsCode;
